import { useMemo, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { widgetAuthQueryFromSearchParams } from '../widget-lib/widget-auth-query';

import { Button } from '@domcrm/embed-ui';

import { listMySubscriptions, type MySubscriptionItem } from '../widget-api/my-subscriptions-list';
import {
  fetchPublicSubscriptionPlans,
  type PublicSubscriptionPlanItem
} from '../widget-api/public-subscription-plans';
import { purchaseSubscriptionPlan } from '../widget-api/subscription-purchase';
import { freezeSubscription, unfreezeSubscription } from '../widget-api/subscription-freeze';
import { ApiError } from '../widget-api/http-client';
import { FutureBookingsNotice, futureBookingsFromApiError, type FutureBookingConflict } from '../widget-ui/future-bookings-notice';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useAsync } from '../widget-hooks/useAsync';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PATH } from '../widget-lib/path-routes';

function statusLabel(s: string): string {
  if (s === 'active') return 'Активен';
  if (s === 'frozen') return 'Заморожен';
  if (s === 'expired') return 'Истёк';
  if (s === 'pending_payment') return 'Ожидает оплаты';
  return s;
}

function visitsProgress(s: MySubscriptionItem): { used: number; total: number; pct: number } | null {
  if (s.visits_total == null || s.visits_total <= 0) return null;
  const used = Math.min(Math.max(0, Number(s.visits_used ?? 0)), s.visits_total);
  const pct = Math.round((used / s.visits_total) * 100);
  return { used, total: s.visits_total, pct };
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  try {
    const end = new Date(`${iso.slice(0, 10)}T23:59:59`);
    const ms = end.getTime() - Date.now();
    return Math.ceil(ms / 86400000);
  } catch {
    return null;
  }
}

function subscriptionWarning(s: MySubscriptionItem): boolean {
  const prog = visitsProgress(s);
  const left = prog ? prog.total - prog.used : null;
  const d = daysUntil(s.valid_until);
  if (left != null && left <= 2) return true;
  if (d != null && d <= 7 && d >= 0) return true;
  return false;
}

function canFreeze(s: MySubscriptionItem): boolean {
  return (
    s.status === 'active' &&
    s.freeze_max_count > 0 &&
    s.freeze_allowed_days > 0 &&
    s.freeze_used_count < s.freeze_max_count &&
    s.freeze_used_days < s.freeze_allowed_days
  );
}

function canUnfreeze(s: MySubscriptionItem): boolean {
  return s.status === 'frozen';
}

type FreezeConfirm = { sub: MySubscriptionItem; action: 'freeze' | 'unfreeze' };

export default function MySubscriptionsPage() {
  useWidgetSeo(
    'Мои абонементы',
    'Информация о ваших действующих и прошлых абонементах, остаток занятий, срок действия и заморозка.'
  );
  const suffix = useQuerySuffix();
  const [searchParams, setSearchParams] = useSearchParams();
  const widgetAuthQuery = useMemo(
    () => widgetAuthQueryFromSearchParams(searchParams),
    [searchParams]
  );
  const {
    accessToken,
    bootstrapHint,
    orgSlug,
    branchId,
    widgetConfig,
    widgetContour,
    organizationId
  } = useEmbedBootstrap();
  const isMobile = widgetContour === 'mobile';

  const effectiveBranchId = useMemo(() => {
    if (!widgetConfig) {
      return branchId;
    }
    if (widgetConfig.branches?.length <= 1) {
      return widgetConfig.branches?.[0]?.id ?? null;
    }
    return branchId;
  }, [widgetConfig, branchId]);

  const needsBranch =
    widgetConfig?.branches != null && widgetConfig.branches.length > 1 && branchId == null;

  const { data, loading, error, reload } = useAsync(
    async () => {
      if (!accessToken) {
        return [];
      }
      return listMySubscriptions(accessToken);
    },
    [accessToken]
  );

  const { data: plans, loading: plansLoading } = useAsync(
    async () => {
      if (!orgSlug || !accessToken) return [];
      return fetchPublicSubscriptionPlans(orgSlug, effectiveBranchId, widgetAuthQuery, accessToken);
    },
    [orgSlug, effectiveBranchId, widgetAuthQuery, accessToken]
  );

  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [purchasingId, setPurchasingId] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [freezeConfirm, setFreezeConfirm] = useState<FreezeConfirm | null>(null);
  const [freezingId, setFreezingId] = useState<number | null>(null);
  const [freezeError, setFreezeError] = useState<string | null>(null);
  const [freezeConflict, setFreezeConflict] = useState<FutureBookingConflict[] | null>(null);

  const returnUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}${window.location.search}`
      : null;

  async function onBuy(plan: PublicSubscriptionPlanItem) {
    setPurchaseError(null);
    setSuccessMsg(null);
    if (!accessToken) {
      setPurchaseError('Войдите, чтобы оформить покупку.');
      return;
    }
    if (!plan.can_buy) {
      setPurchaseError('Этот план сейчас недоступен для покупки.');
      return;
    }
    setPurchasingId(plan.id);
    try {
      const res = await purchaseSubscriptionPlan(plan.id, accessToken, returnUrl);
      if (res.payment_url) {
        window.location.assign(res.payment_url);
        return;
      }
      setSuccessMsg(
        plan.plan_type === 'certificate'
          ? 'Сертификат оформлен. Статус можно посмотреть в «Мои абонементы».'
          : 'Абонемент оформлен. Статус можно посмотреть в «Мои абонементы».'
      );
    } catch (e) {
      if (e instanceof ApiError) {
        setPurchaseError(e.detail);
      } else {
        setPurchaseError(e instanceof Error ? e.message : 'Не удалось оформить покупку');
      }
    } finally {
      setPurchasingId(null);
    }
  }

  const onFreezeConfirm = useCallback(async () => {
    if (!freezeConfirm || !accessToken) return;
    const { sub, action } = freezeConfirm;
    setFreezingId(sub.id);
    setFreezeError(null);
    setFreezeConflict(null);
    try {
      if (action === 'freeze') {
        await freezeSubscription(sub.id, accessToken);
      } else {
        await unfreezeSubscription(sub.id, accessToken);
      }
      setFreezeConfirm(null);
      reload();
    } catch (e) {
      const conflict = futureBookingsFromApiError(e);
      if (conflict) {
        setFreezeConflict(conflict);
      } else if (e instanceof ApiError) {
        setFreezeError(e.detail);
      } else {
        setFreezeError(e instanceof Error ? e.message : 'Не удалось выполнить операцию');
      }
    } finally {
      setFreezingId(null);
    }
  }, [freezeConfirm, accessToken, reload]);

  const subsList = useMemo(() => {
    if (data == null) return [];
    const rawList = Array.isArray(data) ? data : [];
    if (organizationId != null) {
      return rawList.filter((s) => s.organization_id === organizationId);
    }
    return rawList;
  }, [data, organizationId]);

  const activeSubs = useMemo(
    () => subsList.filter((s) => s.status === 'active' || s.status === 'frozen'),
    [subsList]
  );

  const plansList = useMemo(() => {
    if (plans == null) return [];
    return Array.isArray(plans) ? plans : [];
  }, [plans]);

  if (bootstrapHint && !orgSlug && !accessToken) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Мои абонементы</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Мои абонементы</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы увидеть абонементы.</p>
        <Link
          to={`${PATH.signIn}${suffix}`}
          className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)]"
        >
          Войти
        </Link>
      </div>
    );
  }

  const freezeDialog = freezeConfirm ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-[var(--bg-elevated)] p-5 shadow-xl space-y-4">
        <h3 className="text-base font-semibold text-[var(--text)]">
          {freezeConfirm.action === 'freeze' ? 'Заморозить абонемент?' : 'Разморозить абонемент?'}
        </h3>
        <p className="text-sm text-[var(--text-muted)]">
          {freezeConfirm.action === 'freeze'
            ? `Заморозить «${freezeConfirm.sub.plan_name}»? Использовано заморозок: ${freezeConfirm.sub.freeze_used_count} из ${freezeConfirm.sub.freeze_max_count}. Использовано дней: ${freezeConfirm.sub.freeze_used_days} из ${freezeConfirm.sub.freeze_allowed_days}.`
            : `Разморозить «${freezeConfirm.sub.plan_name}»? Текущая заморозка завершится, оставшийся срок абонемента будет продлён.`}
        </p>
        {freezeConflict ? (
          <FutureBookingsNotice bookings={freezeConflict} bookingsHref={`${PATH.bookings}${suffix}`} />
        ) : null}
        {freezeError ? (
          <p className="text-sm text-[var(--error-text)]" role="alert">{freezeError}</p>
        ) : null}
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="secondary"
            disabled={freezingId !== null}
            onClick={() => { setFreezeConfirm(null); setFreezeError(null); setFreezeConflict(null); }}
          >
            Отмена
          </Button>
          <Button
            type="button"
            disabled={freezingId !== null}
            onClick={() => void onFreezeConfirm()}
          >
            {freezingId !== null
              ? 'Выполняется…'
              : freezeConfirm.action === 'freeze'
                ? 'Заморозить'
                : 'Разморозить'}
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  if (isMobile && needsBranch && widgetConfig) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold tracking-tight text-[var(--text)]">Абонементы</h1>
        <p className="text-sm text-[var(--text-muted)]">Выберите филиал для каталога планов.</p>
        <ul className="flex flex-col gap-2">
          {(widgetConfig.branches ?? []).map((b) => (
            <li key={b.id}>
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('branch_id', String(b.id));
                    return next;
                  });
                }}
              >
                {b.name}
              </Button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-6">
        {freezeDialog}
        <div className="flex flex-wrap items-end justify-between gap-2">
          <h1 className="text-2xl font-bold tracking-tight text-[var(--text)]">Абонементы</h1>
        </div>

        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Мои абонементы</h2>
          {loading ? (
            <ul className="flex flex-col gap-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <li key={i} className="h-36 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
              ))}
            </ul>
          ) : error ? (
            <p className="text-sm text-[var(--error-text)]">{error}</p>
          ) : activeSubs.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">Нет активных абонементов.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {activeSubs.map((s) => {
                const prog = visitsProgress(s);
                const warn = subscriptionWarning(s);
                const frozen = s.status === 'frozen';
                const until = s.valid_until
                  ? (() => {
                      try {
                        return new Date(`${s.valid_until.slice(0, 10)}T12:00:00`).toLocaleDateString('ru-RU', {
                          day: 'numeric',
                          month: 'long'
                        });
                      } catch {
                        return s.valid_until.slice(0, 10);
                      }
                    })()
                  : null;
                return (
                  <li
                    key={s.id}
                    className={[
                      'overflow-hidden rounded-2xl bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)] transition-all duration-200 active:scale-[0.98]',
                      warn ? 'ring-2 ring-[var(--warn-border)]' : '',
                      frozen ? 'opacity-80' : ''
                    ].join(' ')}
                  >
                    <div className="flex gap-3">
                      <div
                        className={`w-1 shrink-0 self-stretch rounded-full ${frozen ? 'bg-blue-400' : 'bg-[var(--accent)]'}`}
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-lg font-bold text-[var(--text)]">{s.plan_name}</p>
                          {frozen ? (
                            <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                              ❄ Заморожен
                            </span>
                          ) : null}
                        </div>
                        {prog && !frozen ? (
                          <div>
                            <div className="h-2 overflow-hidden rounded-full bg-[var(--bg-soft)]">
                              <div
                                className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-700 ease-out motion-reduce:transition-none"
                                style={{ width: `${prog.pct}%` }}
                              />
                            </div>
                            <p className="mt-1 text-xs text-[var(--text-muted)]">
                              {prog.used} / {prog.total} занятий
                            </p>
                          </div>
                        ) : !frozen ? (
                          <p className="text-xs text-[var(--text-muted)]">{statusLabel(s.status)}</p>
                        ) : null}
                        {until ? (
                          <p className="text-xs text-[var(--text-muted)]">
                            {frozen ? 'Срок до' : 'Активен до'} {until}
                          </p>
                        ) : null}
                        {warn && !frozen ? (
                          <p className="text-xs font-medium text-[var(--warn-text)]">Скоро закончится — продлите или запишитесь.</p>
                        ) : null}
                        <div className="flex flex-wrap gap-2">
                          {!frozen ? (
                            <Link to={`${PATH.schedule}${suffix}`}>
                              <Button type="button" variant="secondary" className="w-full sm:w-auto">
                                Применить к записи
                              </Button>
                            </Link>
                          ) : null}
                          {canFreeze(s) ? (
                            <Button
                              type="button"
                              variant="secondary"
                              className="w-full sm:w-auto"
                              onClick={() => setFreezeConfirm({ sub: s, action: 'freeze' })}
                            >
                              ❄ Заморозить
                            </Button>
                          ) : null}
                          {canUnfreeze(s) ? (
                            <Button
                              type="button"
                              variant="secondary"
                              className="w-full sm:w-auto"
                              onClick={() => setFreezeConfirm({ sub: s, action: 'unfreeze' })}
                            >
                              ☀ Разморозить
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {orgSlug && (
          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Доступные планы</h2>
            {plansLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-40 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
                ))}
              </div>
            ) : (
              <div className="space-y-3 pt-1">
                {plansList.map((plan) => (
                  <div
                    key={plan.id}
                    className="flex flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]"
                  >
                    <p className="text-base font-bold text-[var(--text)]">{plan.name}</p>
                    <p className="mt-2 text-2xl font-bold tabular-nums text-[var(--accent)]">
                      {plan.price > 0 ? `${plan.price.toLocaleString('ru-RU')} ₽` : 'Бесплатно'}
                    </p>
                    <ul className="mt-2 flex-1 space-y-1 text-xs text-[var(--text-muted)]">
                      {plan.visits_total != null ? <li>• {plan.visits_total} занятий</li> : null}
                      {plan.balance_amount != null ? <li>• Номинал {plan.balance_amount} ₽</li> : null}
                      <li>
                        • Срок {plan.validity_days} дн.
                        {plan.validity_start_type === 'first_use'
                          ? ' с первого использования'
                          : plan.validity_start_type === 'fixed_date'
                            ? ' с момента покупки'
                            : ''}
                      </li>
                    </ul>
                    <div className="mt-3">
                      <Button
                        type="button"
                        className="w-full transition-all duration-200 active:scale-[0.97]"
                        disabled={!plan.can_buy || purchasingId !== null}
                        onClick={() => void onBuy(plan)}
                      >
                        {purchasingId === plan.id ? 'Оформление…' : plan.price > 0 ? 'Купить' : 'Получить'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {purchaseError ? (
              <p className="text-sm text-[var(--error-text)]" role="alert">
                {purchaseError}
              </p>
            ) : null}
            {successMsg ? (
              <p className="text-sm text-[var(--text-muted)]" role="status">
                {successMsg}
              </p>
            ) : null}
          </section>
        )}

        {orgSlug && (
          <p className="text-center text-xs text-[var(--text-muted)]">
            <Link className="font-medium text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.subscriptionPlans}${suffix}`}>
              Все планы в каталоге
            </Link>
          </p>
        )}
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="px-4 py-6">
        <h1 className="text-xl font-semibold tracking-tight">Абонементы</h1>
        <p className="mt-2 text-sm text-[var(--text-muted)]">Абонементы доступны после входа в личный кабинет.</p>
        <Link className="mt-4 inline-flex h-9 items-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)]" to={`${PATH.signIn}${suffix}`}>
          Войти
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full min-w-0">
      {freezeDialog}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Мои абонементы</h1>
        <div className="flex flex-wrap gap-3 text-sm">
          {orgSlug && (
            <Link className="text-[var(--accent)]" to={`${PATH.subscriptionPlans}${suffix}`}>
              Каталог
            </Link>
          )}
          {orgSlug && (
            <Link className="text-[var(--accent)]" to={`${PATH.schedule}${suffix}`}>
              Расписание
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <ul className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="space-y-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4">
              <div className="h-4 w-44 animate-pulse rounded bg-[var(--bg-elevated)]" />
              <div className="h-3 w-56 animate-pulse rounded bg-[var(--bg-elevated)]" />
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <p className="text-sm text-[var(--error-text)]">{error}</p>
      ) : null}

      {!loading && !error && subsList.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">У вас пока нет абонементов.</p>
      ) : null}

      {subsList.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {subsList.map((s) => {
            const frozen = s.status === 'frozen';
            return (
              <li
                key={s.id}
                className={[
                  'rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 text-sm',
                  frozen ? 'opacity-80' : ''
                ].join(' ')}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[var(--text)]">{s.plan_name}</p>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">
                      {statusLabel(s.status)}
                      {s.visits_total != null
                        ? ` · осталось визитов: ${Math.max(0, Number(s.visits_total) - Number(s.visits_used ?? 0))}`
                        : s.balance_total != null
                          ? ` · остаток: ${Math.max(0, Number(s.balance_total) - Number(s.balance_used ?? 0))} ₽`
                          : ''}
                      {s.valid_until ? ` · до ${s.valid_until.slice(0, 10)}` : ''}
                      {s.branch_name ? ` · ${s.branch_name}` : ''}
                    </p>
                    {s.freeze_max_count > 0 ? (
                      <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                        Заморозок: {s.freeze_used_count}/{s.freeze_max_count} · дней: {s.freeze_used_days}/{s.freeze_allowed_days}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {canFreeze(s) ? (
                      <button
                        type="button"
                        className="rounded-lg p-1.5 text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-blue-600 transition-colors"
                        title="Заморозить"
                        aria-label={`Заморозить ${s.plan_name}`}
                        onClick={() => setFreezeConfirm({ sub: s, action: 'freeze' })}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93 4.93 19.07" />
                        </svg>
                      </button>
                    ) : null}
                    {canUnfreeze(s) ? (
                      <button
                        type="button"
                        className="rounded-lg p-1.5 text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-amber-600 transition-colors"
                        title="Разморозить"
                        aria-label={`Разморозить ${s.plan_name}`}
                        onClick={() => setFreezeConfirm({ sub: s, action: 'unfreeze' })}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="4" />
                          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
